/** See `src/i18n/locales/en/overview.ts`. */
export const overview = {
  page: {
    title: 'Übersicht',
    lead: 'Wie geht es {{company}}?',
    noScope: 'Wählen Sie ein Unternehmen, um die Übersicht zu sehen.',
    noDepartments: 'Keine Abteilung hier hat Statistiken, die Sie sehen dürfen.',
    disclaimer:
      'Alle Werte sind indikative Wohlbefindens-Einblicke auf Gruppenebene und nicht als medizinische Diagnose gedacht.',
  },
  summary: {
    caption: 'KI-Zusammenfassung auf Basis anonymisierter, aggregierter Daten.',
  },
  figures: {
    vitality: 'Vitalität',
    stress: 'Stress',
    sleep: 'Schlaf',
    engagement: 'Engagement',
    activeParticipants: 'Aktive Teilnehmer',
  },
  figuresInfo: {
    vitality: 'Ein zusammengesetzter Wert aus Schlaf, Aktivität, HRV, Stress und Herzfrequenz.',
    stress: 'Durchschnittliches Stressniveau, direkt vom Wearable gemessen.',
    sleep: 'Durchschnittliche Schlafdauer pro Nacht, direkt vom Wearable gemessen.',
    engagement: 'Anteil der Mitarbeiter, die die App aktiv nutzen.',
    activeParticipants: 'Anzahl der Mitarbeiter, die im letzten Zeitraum aktiv waren.',
  },
  legend: {
    title: 'Was Sie sehen',
    measured:
      'Direkt gemessen — direkt von der NOLATR-Wearable oder einem verbundenen Sensor. Höchste Zuverlässigkeit.',
    aiDerived:
      'KI-abgeleitet — keine direkte Messung. Die KI kombiniert mehrere Signale zu einem Indikator.',
    usage: 'Nutzung & Verhalten — tatsächliche Interaktionen in der App, keine Gesundheitsmessung.',
  },
  developments: {
    title: 'Wichtigste Entwicklungen',
    empty: 'Keine besonderen Entwicklungen in dieser Periode.',
    stress: {
      title: 'Gelegenheit, dem Team Ruhe zu bieten (Stress erhöht)',
      body: 'Ein freundlicher Moment der Ruhe oder ein Atemtipp kann dem Team helfen, wieder aufzutanken.',
    },
    sleep: {
      title: 'Gelegenheit: Schlaf unter dem Ziel',
      body: 'Ein gezielter, positiver Erholungstipp kann das Team gut unterstützen.',
    },
    vitalityGood: {
      title: 'Die Vitalität entwickelt sich gut',
      body: 'Das Team hält sein aktuelles Tempo gut — ein guter Moment, um das anzuerkennen.',
    },
    needsReactivation: {
      title: '{{count}} Personen müssen reaktiviert werden',
      body: 'Eine kurze, persönliche Reaktivierungsnachricht funktioniert meist am besten.',
    },
    mostActiveWeekday: {
      title: 'Die meiste Energie am {{weekday}}',
      body: 'Planen Sie Challenges und Kommunikation rund um den aktivsten Tag für maximale Reichweite.',
    },
    challengeParticipation: {
      title: 'Challenge-Teilnahme {{pct}}%',
      body: 'Eine neue, niedrigschwellige Team-Challenge könnte mehr Menschen zur Teilnahme bewegen.',
    },
  },
  actions: {
    title: 'Aktionen für HR',
    empty: 'Derzeit keine empfohlenen Aktionen.',
    sendReminder: {
      title: '{{count}} Mitarbeiter noch nicht aktiviert',
      subtitle: 'Erinnerung senden',
    },
    reactivate: {
      title: '{{count}} Mitarbeiter müssen reaktiviert werden',
      subtitle: 'Kontakt aufnehmen',
    },
    stressCheckIn: {
      title: 'Erhöhter Stress im Team',
      subtitle: 'Kurzes Gespräch über die Arbeitsbelastung erwägen',
    },
    sleepReminder: {
      title: 'Schlaf unter dem Ziel im Team',
      subtitle: 'Erinnerung an Abendroutinen und Bildschirmzeit erwägen',
    },
    activeChallenge: {
      title: '„{{name}}“ läuft — planen Sie die nächste',
      subtitle: 'Challenges',
    },
    pilotEvaluation: {
      interim: {
        title: 'Pilot „{{name}}“ ist bereit für die Zwischenevaluation',
        subtitle: 'Pilot ansehen',
      },
      final: {
        title: 'Pilot „{{name}}“ ist bereit für die Abschlussevaluation',
        subtitle: 'Pilot ansehen',
      },
    },
  },
  energy: {
    title: 'Energie & Arbeitsgefühl',
    subtitle:
      'Wie das Team den Arbeitstag erlebt — Energie, Pausen, Arbeitsrhythmus und Engagement. Nur aggregiert, nie individuell.',
    badge: 'Aggregiert & anonymisiert',
    tbd: 'Noch zu bestimmen',
    missingData: {
      noSource: 'Energie-Index und eNPS: Datenquelle noch nicht festgelegt.',
      insufficientParticipants: 'Nicht genügend Teilnehmende für {{metrics}}.',
    },
    privacyNote:
      'Unterstützende Einblicke, keine Überwachung. Indikatives Wohlbefinden auf Gruppenebene — keine medizinische Einordnung.',
    metrics: {
      energyIndex: 'Energie-Index',
      breakAdoption: 'Pausen-Adoption',
      focusRhythm: 'Fokusrhythmus',
      coachAdoption: 'Coach-Adoption',
      enps: 'eNPS',
    },
  },
  pilots: {
    title: 'Pilotstatus',
    empty: 'Noch keine Piloten.',
    participantsAndDay: '{{count}} Teilnehmende · Tag {{day}} von {{total}}',
    viewReport: 'Pilotbericht ansehen',
  },
  challenges: {
    title: 'Aktive Challenges',
    empty: 'Noch keine Challenges.',
    active: 'Aktiv',
    total: 'Gesamt',
    participants: 'Teilnehmer',
    activeBadge: '{{count}} aktiv',
    manageLink: 'Challenges verwalten',
    noneActive: 'Im Moment keine aktiven Challenges.',
    participantsCount: '{{count}} Teilnehmer',
  },
  trends: {
    title: 'Trends',
    filters: {
      allDepartments: 'Alle Abteilungen',
      participants: {
        label: 'Teilnehmer',
        all: 'Alle Teilnehmer',
        manager: 'Führungskräfte',
        employee: 'Mitarbeitende',
      },
    },
    vitalityScore: 'Vitalitätswert im Zeitverlauf',
    activeUsersPerWeek: 'Aktive Nutzer pro Woche',
    sleep: 'Schlaftrend',
    stress: 'Stresstrend',
    hrv: 'HRV-Trend',
    recovery: 'Erholungstrend',
    activity: 'Aktivitätstrend',
    notAvailable: 'Noch keine Datenquelle für diesen Trend — in dieser Version nicht verfügbar.',
    vsPreviousPeriod: 'vs. vorheriger Zeitraum',
    info: {
      vitalityScore:
        'Ein zusammengesetzter Vitalitätswert pro Tag, basierend auf allen Vitalitätssignalen.',
      activeUsersPerWeek:
        'Anzahl der eindeutigen Mitarbeiter, die die App pro Woche aktiv genutzt haben.',
      sleep: 'Durchschnittliche Schlafdauer pro Tag, direkt vom Wearable gemessen.',
      stress: 'Durchschnittliches Stressniveau pro Tag, direkt vom Wearable gemessen.',
      hrv: 'Durchschnittliche Herzfrequenzvariabilität (HRV) pro Tag, ein Indikator für Erholung.',
      recovery:
        'Ein zusammengesetzter Erholungswert pro Tag, basierend auf Schlaf, HRV und Aktivität.',
      activity: 'Durchschnittliche Schrittzahl pro Tag, direkt vom Wearable gemessen.',
    },
  },
} as const;
